import React, { useRef } from "react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";

// A <th> with: a sort dropdown (Ascending / Descending / Clear) on the label, a visible
// sort-direction indicator, and a drag-to-resize handle on the right edge. Pinned columns
// stay sticky (style provided by the caller). Shared by DataTable and PrefTable.
export default function ColumnHeader({ col, style, className, sticky, onResize, sort, onSort }) {
  const start = useRef(null);
  const onDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const th = e.currentTarget.closest("th");
    start.current = { x: e.clientX, w: th ? th.getBoundingClientRect().width : 160 };
    const move = (ev) => { if (start.current) onResize(col.key, start.current.w + (ev.clientX - start.current.x)); };
    const up = () => { start.current = null; document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); document.body.style.userSelect = ""; };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.body.style.userSelect = "none";
  };
  const pinClass = sticky ? `bg-slate-50 ${sticky.last ? "shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]" : ""} ${sticky.first ? "shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.15)]" : ""}` : "";
  const sortable = col.sortable !== false && !!onSort;
  const active = sort && sort.key === col.key ? sort.dir : null;
  const alignEnd = col.align === "right";

  const label = sortable ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={`col-sort-${col.key}`}
          className={`group/sort inline-flex items-center gap-1 font-medium text-slate-500 hover:text-slate-800 transition-colors ${alignEnd ? "flex-row-reverse" : ""}`}>
          <span>{col.label}</span>
          {active === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-indigo-600" />
            : active === "desc" ? <ArrowDown className="w-3.5 h-3.5 text-indigo-600" />
            : <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300 opacity-0 group-hover/sort:opacity-100 transition-opacity" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={alignEnd ? "end" : "start"} className="w-44">
        <DropdownMenuItem onClick={() => onSort(col.key, "asc")} data-testid={`col-sort-asc-${col.key}`}>
          <ArrowUp className="w-4 h-4 mr-2 text-slate-500" /> Sort Ascending
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onSort(col.key, "desc")} data-testid={`col-sort-desc-${col.key}`}>
          <ArrowDown className="w-4 h-4 mr-2 text-slate-500" /> Sort Descending
        </DropdownMenuItem>
        {active && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => onSort(col.key, null)} data-testid={`col-sort-clear-${col.key}`}>Clear sort</DropdownMenuItem></>}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : <span>{col.label}</span>;

  return (
    <th style={style} data-testid={`col-head-${col.key}`}
      className={`group relative px-6 py-3.5 whitespace-nowrap ${alignEnd ? "text-right" : ""} ${pinClass} ${className || ""}`}>
      <div className={`flex items-center ${alignEnd ? "justify-end" : ""}`}>{label}</div>
      <span onMouseDown={onDown} data-testid={`col-resize-${col.key}`}
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none opacity-0 group-hover:opacity-100 bg-indigo-300/60 hover:bg-indigo-500 transition-opacity" />
    </th>
  );
}

// Client-side comparator for PrefTable (full in-memory rows).
export function sortRows(rows, sort, columns) {
  if (!sort || !sort.dir) return rows || [];
  const col = (columns || []).find((c) => c.key === sort.key);
  const val = (r) => (col && col.sortValue ? col.sortValue(r) : r[sort.key]);
  const empty = (v) => v === null || v === undefined || v === "";
  const factor = sort.dir === "desc" ? -1 : 1;
  const arr = [...(rows || [])];
  arr.sort((a, b) => {
    let x = val(a), y = val(b);
    const xe = empty(x), ye = empty(y);
    if (xe && ye) return 0;
    if (xe) return 1;    // empties ALWAYS last, regardless of direction
    if (ye) return -1;
    let cmp;
    if (typeof x === "number" && typeof y === "number") cmp = x - y;
    else {
      const nx = Number(x), ny = Number(y);
      cmp = (!Number.isNaN(nx) && !Number.isNaN(ny))
        ? nx - ny
        : String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: "base" });
    }
    return cmp * factor;
  });
  return arr;
}
